import React, { useEffect, useState } from 'react';
import { auth, db, functions } from '../../firebase/config';
import { doc, onSnapshot, collection, query, where, getDocs, updateDoc, getDoc, addDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import PhoneVerification from './PhoneVerification'; 

export default function Profile() {
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory');
  
  const [activeListings, setActiveListings] = useState([]); 
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [savedItems, setSavedItems] = useState([]); 
  const [wishlist, setWishlist] = useState([]); 
  
  const [trackingInputs, setTrackingInputs] = useState({});
  const [paypalEmail, setPaypalEmail] = useState(''); 
  const [loading, setLoading] = useState(true);
  const [generatingLabel, setGeneratingLabel] = useState(null); 

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewItem, setReviewItem] = useState(null);
  const [rating, setRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  // 🚨 NEW: Full Listing Editor State
  const [editingItem, setEditingItem] = useState(null);
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);

  const categories = ['General', 'Electronics & Computers', 'Apparel & Accessories', 'Home & Furniture', 'Automotive', 'Toys & Hobbies'];

  useEffect(() => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(doc(db, 'users', auth.currentUser.uid), (docSnap) => {
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
    });

    const fetchOrderData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists() && userDoc.data().paypalEmail) {
          setPaypalEmail(userDoc.data().paypalEmail);
        }

        const inventoryQuery = query(collection(db, 'listings'), where('sellerId', '==', auth.currentUser.uid), where('status', '==', 'active'));
        const purchaseQuery = query(collection(db, 'listings'), where('buyerId', '==', auth.currentUser.uid));
        const salesQuery = query(collection(db, 'listings'), where('sellerId', '==', auth.currentUser.uid), where('status', '==', 'sold'));
        const wishlistQuery = query(collection(db, 'wishlists'), where('userId', '==', auth.currentUser.uid));

        const [invSnap, purSnap, salSnap, wishSnap] = await Promise.all([
          getDocs(inventoryQuery), 
          getDocs(purchaseQuery), 
          getDocs(salesQuery),
          getDocs(wishlistQuery)
        ]);

        setActiveListings(invSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setPurchases(purSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setSales(salSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setWishlist(wishSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      } catch (error) {
        console.error("Error fetching order data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchSavedItems = async () => {
      if (userData?.favorites && userData.favorites.length > 0) {
        try {
          const favPromises = userData.favorites.map(id => getDoc(doc(db, 'listings', id)));
          const favSnaps = await Promise.all(favPromises);
          
          const validItems = favSnaps
            .filter(snap => snap.exists() && snap.data().status === 'active')
            .map(snap => ({ id: snap.id, ...snap.data() }));
            
          setSavedItems(validItems);
        } catch (error) {
          console.error("Error fetching saved items:", error);
        }
      } else {
        setSavedItems([]);
      }
    };
    
    fetchSavedItems();
  }, [userData?.favorites]);

  const handleTrackingChange = (itemId, value) => {
    setTrackingInputs(prev => ({ ...prev, [itemId]: value }));
  };

  const handleSaveTracking = async (itemId) => {
    const trackingNumber = trackingInputs[itemId];
    if (!trackingNumber || trackingNumber.trim() === "") return alert("Please enter a valid tracking number.");
    try {
      await updateDoc(doc(db, 'listings', itemId), { trackingNumber });
      setSales(prevSales => prevSales.map(item => item.id === itemId ? { ...item, trackingNumber } : item));
      alert("Tracking number saved! The buyer's dashboard has been updated.");
    } catch (error) {
      console.error("Error saving tracking:", error);
      alert("Could not save tracking number. Please try again.");
    }
  };

  const handleCreateLabel = async (itemId) => {
    setGeneratingLabel(itemId);
    try {
      const createLabel = httpsCallable(functions, 'createShippingLabel');
      const result = await createLabel({ itemId, weight: 2, length: 10, width: 8, height: 4 });
      
      window.open(result.data.labelUrl, '_blank');
      alert("Label generated and tracking updated!");
      
      setSales(prev => prev.map(s => s.id === itemId ? { ...s, trackingNumber: result.data.trackingNumber, shippingLabelUrl: result.data.labelUrl } : s));
    } catch (error) {
      alert("Error creating label. Please check your Shippo configuration or enter tracking manually.");
    } finally {
      setGeneratingLabel(null);
    }
  };

  const handleSavePayPalEmail = async () => {
    if (!paypalEmail.includes('@')) return alert("Please enter a valid email address.");
    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { paypalEmail: paypalEmail.trim() });
      setUserData(prev => ({ ...prev, paypalEmail: paypalEmail.trim() }));
      alert("PayPal payout email successfully saved!");
    } catch (error) {
      console.error("Error saving PayPal email:", error);
      alert("Could not save email. Please try again.");
    }
  };

  const deleteWishlistSearch = async (id) => {
    if (window.confirm("Delete this saved search? You will no longer receive email alerts for this item.")) {
      try {
        await deleteDoc(doc(db, "wishlists", id));
        setWishlist(prev => prev.filter(w => w.id !== id));
      } catch (error) {
        alert("Failed to delete saved search.");
      }
    }
  };

  const openReviewModal = (item) => {
    setReviewItem(item);
    setRating(5);
    setReviewComment('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);

    try {
      await addDoc(collection(db, 'reviews'), {
        listingId: reviewItem.id,
        sellerId: reviewItem.sellerId,
        buyerId: auth.currentUser.uid,
        rating: rating,
        comment: reviewComment,
        createdAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'listings', reviewItem.id), { reviewLeft: true });
      setPurchases(prevPurchases => prevPurchases.map(p => p.id === reviewItem.id ? { ...p, reviewLeft: true } : p));
      
      setShowReviewModal(false);
      alert("Thank you! Your review has been published to the seller's storefront.");
    } catch (error) {
      console.error("Error leaving review:", error);
      alert("Could not submit review. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Are you sure you want to delete this listing? This cannot be undone.")) {
      try {
        await deleteDoc(doc(db, 'listings', itemId));
        setActiveListings(prev => prev.filter(item => item.id !== itemId));
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Failed to delete item.");
      }
    }
  };

  // 🚨 NEW: Full Listing Update Logic
  const handleUpdateListing = async (e) => {
    e.preventDefault();
    setIsUpdatingItem(true);
    try {
      const { id, title, price, category, condition, brand, description, location, shippingAvailable, shippingCost } = editingItem;
      
      await updateDoc(doc(db, 'listings', id), {
        title,
        price: Number(price),
        category: category || 'General',
        condition: condition || 'Used - Good',
        brand: brand || '',
        description,
        location,
        shippingAvailable,
        shippingCost: shippingAvailable ? Number(shippingCost) : 0
      });

      setActiveListings(prev => prev.map(item => item.id === id ? { 
        ...item, 
        title, 
        price: Number(price), 
        category, 
        condition, 
        brand, 
        description, 
        location, 
        shippingAvailable, 
        shippingCost: shippingAvailable ? Number(shippingCost) : 0 
      } : item));
      
      setEditingItem(null);
      alert("Listing updated successfully!");
    } catch (error) {
      console.error("Error updating item:", error);
      alert("Failed to update listing.");
    } finally {
      setIsUpdatingItem(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#0f172a', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '18px' }}>Loading Dashboard...</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '40px 20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '16px' }}>
          <h1 style={{ margin: 0, fontSize: '32px', color: '#0f172a', fontWeight: '800', letterSpacing: '-1px' }}>My Dashboard</h1>
          <div style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '8px 16px', borderRadius: '50px', fontWeight: '700', fontSize: '14px', border: '1px solid #fde68a', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            Seller Rating: {userData?.rating ? `${userData.rating} ⭐ (${userData.reviewCount})` : 'New Seller'}
          </div>
        </div>

        {/* FINANCIAL DASHBOARD */}
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '20px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)', marginBottom: '30px', border: '1px solid #e2e8f0' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#0f172a', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>💳 Financial Settings</h2>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#0f172a' }}>PayPal Payouts</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Where should we send your funds when your items sell?</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input 
                type="email" 
                placeholder="Your PayPal Email" 
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                style={{ padding: '12px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', width: '250px', outline: 'none' }}
                onFocus={handleFocus} onBlur={handleBlur}
              />
              <button 
                onClick={handleSavePayPalEmail} 
                disabled={userData?.paypalEmail === paypalEmail.trim() && paypalEmail !== ''}
                style={{ 
                  padding: '12px 24px', 
                  backgroundColor: userData?.paypalEmail === paypalEmail.trim() && paypalEmail !== '' ? '#10b981' : '#4f46e5', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  fontWeight: '700', 
                  cursor: userData?.paypalEmail === paypalEmail.trim() && paypalEmail !== '' ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              >
                {userData?.paypalEmail === paypalEmail.trim() && paypalEmail !== '' ? '✓ Saved' : 'Save Update'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          
          {/* LEFT COLUMN: NAVIGATION & PROFILE */}
          <aside style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '24px', position: 'sticky', top: '24px' }}>
            
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '20px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button onClick={() => setActiveTab('inventory')} style={tabStyle(activeTab === 'inventory')}>📦 Active Inventory <span style={badgeStyle}>{activeListings.length}</span></button>
              <button onClick={() => setActiveTab('sales')} style={tabStyle(activeTab === 'sales')}>🏷️ Fulfillment <span style={badgeStyle}>{sales.length}</span></button>
              <button onClick={() => setActiveTab('purchases')} style={tabStyle(activeTab === 'purchases')}>🛍️ My Purchases <span style={badgeStyle}>{purchases.length}</span></button>
              <button onClick={() => setActiveTab('saved')} style={tabStyle(activeTab === 'saved')}>❤️ Saved Gear <span style={badgeStyle}>{savedItems.length}</span></button>
              <button onClick={() => setActiveTab('wishlist')} style={tabStyle(activeTab === 'wishlist')}>🔔 My Alerts <span style={badgeStyle}>{wishlist.length}</span></button>
            </div>

            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Profile Details</h3>
              <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#0f172a' }}><strong>Email:</strong> {userData?.email}</p>
              <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#0f172a' }}><strong>Location:</strong> {userData?.location || 'Not set'}</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#0f172a' }}><strong>Joined:</strong> {userData?.joinedAt?.toDate().toLocaleDateString()}</p>
            </div>

            <Link to="/bulk-import" style={{ padding: '16px', backgroundColor: '#0f172a', color: 'white', textDecoration: 'none', borderRadius: '16px', fontWeight: '700', textAlign: 'center', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', transition: 'background-color 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#1e293b'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#0f172a'}>
              🚀 Bulk Import (CSV)
            </Link>

            <PhoneVerification user={auth.currentUser} isAlreadyVerified={userData?.isVerified} />

          </aside>

          {/* RIGHT COLUMN: DYNAMIC CONTENT */}
          <main style={{ flex: '2 1 600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
              
              {activeTab === 'inventory' && (
                <>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>Active Inventory</h3>
                  {activeListings.length === 0 ? <p style={{ color: '#64748b', fontSize: '15px' }}>You have no active listings.</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {activeListings.map(item => (
                        <div key={item.id} style={{ display: 'flex', gap: '20px', padding: '20px', border: '1px solid #e2e8f0', borderRadius: '16px', backgroundColor: '#f8fafc', alignItems: 'center', flexWrap: 'wrap', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                          onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.05)'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                          onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                          <img src={item.mainImage || item.imageUrls?.[0]} alt={item.title} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e2e8f0' }} />
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <Link to={`/item/${item.id}`} style={{ textDecoration: 'none', color: '#0f172a' }}><h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' }}>{item.title}</h4></Link>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <p style={{ margin: 0, fontSize: '20px', color: '#059669', fontWeight: '800' }}>${Number(item.price).toFixed(2)}</p>
                              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#e2e8f0', padding: '4px 10px', borderRadius: '6px' }}>
                                <span style={{ fontSize: '14px' }}>👁️</span> {item.views || 0} views
                              </p>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {/* 🚨 THE NEW EDIT BUTTON */}
                            <button onClick={() => setEditingItem(item)} style={{ padding: '10px 20px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', transition: 'background-color 0.2s', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)' }}
                              onMouseOver={e => e.currentTarget.style.backgroundColor = '#4338ca'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4f46e5'}>
                              ✏️ Edit Listing
                            </button>
                            <button onClick={() => handleDeleteItem(item.id)} style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', transition: 'background-color 0.2s' }}
                              onMouseOver={e => e.currentTarget.style.backgroundColor = '#fef2f2'} onMouseOut={e => e.currentTarget.style.backgroundColor = 'white'}>
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ... (Sales, Purchases, Saved, Wishlist Tabs remain visually identical but use premium colors) ... */}
              {activeTab === 'sales' && (
                <>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>Fulfillment Dashboard</h3>
                  {sales.length === 0 ? <p style={{ color: '#64748b', fontSize: '15px' }}>You haven't sold any items yet.</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {sales.map(item => (
                        <div key={item.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', padding: '20px', border: '1px solid #e2e8f0', borderRadius: '16px', backgroundColor: '#f8fafc', alignItems: 'center' }}>
                          <img src={item.mainImage || item.imageUrls?.[0]} alt={item.title} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '12px' }} />
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <Link to={`/item/${item.id}`} style={{ textDecoration: 'none', color: '#0f172a' }}><h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' }}>{item.title}</h4></Link>
                            <p style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#64748b' }}>Sale Price: <strong style={{ color: '#059669' }}>${Number(item.price).toFixed(2)}</strong></p>
                            {item.shippingAddress && (
                              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px', fontSize: '13px', border: '1px solid #cbd5e1', color: '#475569', lineHeight: '1.5' }}>
                                <strong style={{ color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}>Ship To:</strong><br />{item.buyerName}<br />{item.shippingAddress.address_line_1} {item.shippingAddress.address_line_2 && <span><br />{item.shippingAddress.address_line_2}</span>}<br />{item.shippingAddress.admin_area_2}, {item.shippingAddress.admin_area_1} {item.shippingAddress.postal_code}
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: '250px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {item.trackingNumber ? (
                               <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                                 <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#065f46', fontWeight: '700', textTransform: 'uppercase' }}>Tracking Saved:</p>
                                 <strong style={{ color: '#047857', fontSize: '16px' }}>{item.trackingNumber}</strong>
                                 {item.shippingLabelUrl && (
                                    <a href={item.shippingLabelUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '8px', color: '#059669', fontWeight: '700', fontSize: '13px', textDecoration: 'none', padding: '6px 12px', backgroundColor: '#d1fae5', borderRadius: '6px' }}>
                                      🖨️ Re-print Label
                                    </a>
                                 )}
                               </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <button 
                                  onClick={() => handleCreateLabel(item.id)}
                                  disabled={generatingLabel === item.id}
                                  style={{ padding: '12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                >
                                  {generatingLabel === item.id ? 'Generating PDF...' : '📦 Buy & Print Label'}
                                </button>
                                <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', fontWeight: '700', letterSpacing: '1px' }}>— OR MANUALLY ENTER —</div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <input type="text" placeholder="Tracking #" value={trackingInputs[item.id] || ""} onChange={(e) => handleTrackingChange(item.id, e.target.value)} style={{ flex: 1, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none' }} onFocus={handleFocus} onBlur={handleBlur} />
                                  <button onClick={() => handleSaveTracking(item.id)} style={{ padding: '10px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>Save</button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'purchases' && (
                <>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>My Order History</h3>
                  {purchases.length === 0 ? <p style={{ color: '#64748b', fontSize: '15px' }}>You haven't bought any gear yet.</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {purchases.map(item => (
                        <div key={item.id} style={{ display: 'flex', gap: '20px', padding: '20px', border: '1px solid #e2e8f0', borderRadius: '16px', backgroundColor: '#f8fafc', alignItems: 'center', flexWrap: 'wrap' }}>
                          <img src={item.mainImage || item.imageUrls?.[0]} alt={item.title} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '12px' }} />
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <Link to={`/item/${item.id}`} style={{ textDecoration: 'none', color: '#0f172a' }}><h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' }}>{item.title}</h4></Link>
                            <p style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#059669', fontWeight: '800' }}>${Number(item.price).toFixed(2)}</p>
                            {item.trackingNumber ? (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#eff6ff', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', color: '#1e40af', border: '1px solid #bfdbfe', fontWeight: '600' }}><strong>🚚 Shipped:</strong> {item.trackingNumber}</div>
                            ) : (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#fef3c7', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', color: '#92400e', border: '1px solid #fde68a', fontWeight: '600' }}><strong>⏳ Processing:</strong> Awaiting shipment</div>
                            )}
                          </div>
                          {item.trackingNumber && !item.reviewLeft && (
                            <div style={{ flexShrink: 0 }}>
                              <button onClick={() => openReviewModal(item)} style={{ padding: '12px 20px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '50px', fontWeight: '800', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)' }}>⭐ Leave a Review</button>
                            </div>
                          )}
                          {item.reviewLeft && <div style={{ flexShrink: 0 }}><span style={{ fontSize: '14px', color: '#10b981', fontWeight: '800', padding: '8px 16px', backgroundColor: '#d1fae5', borderRadius: '50px' }}>✓ Reviewed</span></div>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'saved' && (
                <>
                  <h3 style={{ margin: '0 0 24px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>Saved Gear</h3>
                  {savedItems.length === 0 ? <p style={{ color: '#64748b', fontSize: '15px' }}>You haven't saved any items yet.</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {savedItems.map(item => (
                        <div key={item.id} style={{ display: 'flex', gap: '16px', padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', backgroundColor: 'white', alignItems: 'center' }}>
                          <img src={item.mainImage || item.imageUrls?.[0]} alt={item.title} style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px' }} />
                          <div style={{ flex: 1 }}>
                            <Link to={`/item/${item.id}`} style={{ textDecoration: 'none', color: '#0f172a' }}><h4 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '700' }}>{item.title}</h4></Link>
                            <p style={{ margin: 0, fontSize: '15px', color: '#059669', fontWeight: '800' }}>${Number(item.price).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'wishlist' && (
                <>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>My Alerts</h3>
                  <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '15px' }}>We will email you the second gear matching these searches hits the platform.</p>
                  
                  {wishlist.length === 0 ? <p style={{ color: '#64748b', fontSize: '15px' }}>You have no saved searches yet.</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {wishlist.map(w => (
                        <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                          <div>
                            <strong style={{ fontSize: '18px', color: '#0f172a' }}>"{w.searchTerm}"</strong>
                            <span style={{ marginLeft: '12px', color: '#475569', fontSize: '12px', backgroundColor: '#e2e8f0', padding: '4px 10px', borderRadius: '6px', fontWeight: '700', textTransform: 'uppercase' }}>In: {w.category}</span>
                          </div>
                          <button onClick={() => deleteWishlistSearch(w.id)} style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: '700', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px' }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#fef2f2'} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

            </div>
          </main>
        </div>
      </div>

      {/* 🚨 THE NEW FULL EDIT MODAL */}
      {editingItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
          <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '24px', width: '100%', maxWidth: '700px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '24px', color: '#0f172a', fontWeight: '800' }}>Edit Listing</h3>
              <button onClick={() => setEditingItem(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>
            
            <form onSubmit={handleUpdateListing} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div>
                <label style={labelStyle}>Item Title</label>
                <input type="text" value={editingItem.title} onChange={e => setEditingItem({...editingItem, title: e.target.value})} required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={editingItem.category} onChange={e => setEditingItem({...editingItem, category: e.target.value})} required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Condition</label>
                  <select value={editingItem.condition} onChange={e => setEditingItem({...editingItem, condition: e.target.value})} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}>
                    <option value="New">Brand New</option>
                    <option value="Used - Like New">Used - Like New</option>
                    <option value="Used - Excellent">Used - Excellent</option>
                    <option value="Used - Good">Used - Good</option>
                    <option value="Used - Fair">Used - Fair</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>Price ($)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: '700' }}>$</span>
                    <input type="number" step="0.01" value={editingItem.price} onChange={e => setEditingItem({...editingItem, price: e.target.value})} required style={{...inputStyle, paddingLeft: '32px'}} onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Brand (Optional)</label>
                  <input type="text" value={editingItem.brand || ''} onChange={e => setEditingItem({...editingItem, brand: e.target.value})} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Item Location</label>
                <input type="text" value={editingItem.location} onChange={e => setEditingItem({...editingItem, location: e.target.value})} required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
              </div>

              <div style={{ padding: '20px', backgroundColor: editingItem.shippingAvailable ? '#eff6ff' : '#f8fafc', borderRadius: '16px', border: `1px solid ${editingItem.shippingAvailable ? '#bfdbfe' : '#e2e8f0'}`, transition: 'all 0.2s' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>
                  <input type="checkbox" checked={editingItem.shippingAvailable} onChange={e => setEditingItem({...editingItem, shippingAvailable: e.target.checked})} style={{ width: '18px', height: '18px', accentColor: '#4f46e5', cursor: 'pointer' }} />
                  I am willing to ship this item
                </label>
                {editingItem.shippingAvailable && (
                  <div style={{ marginTop: '16px' }}>
                    <label style={labelStyle}>Shipping Cost ($)</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: '700' }}>$</span>
                      <input type="number" step="0.01" value={editingItem.shippingCost || ''} onChange={e => setEditingItem({...editingItem, shippingCost: e.target.value})} required style={{...inputStyle, paddingLeft: '32px'}} onFocus={handleFocus} onBlur={handleBlur} />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={editingItem.description} onChange={e => setEditingItem({...editingItem, description: e.target.value})} required style={{ ...inputStyle, height: '120px', resize: 'vertical' }} onFocus={handleFocus} onBlur={handleBlur} />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                <button type="button" onClick={() => setEditingItem(null)} style={{ flex: 1, padding: '16px', backgroundColor: 'transparent', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={isUpdatingItem} style={{ flex: 2, padding: '16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: isUpdatingItem ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)' }}>{isUpdatingItem ? 'Saving Changes...' : 'Save Updates'}</button>
              </div>
              <p style={{ margin: 0, textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>*Note: To change photos, please delete and recreate the listing.</p>
            </form>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && reviewItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '24px', width: '100%', maxWidth: '450px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>Rate Your Experience</h3>
            <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '15px' }}>How was the transaction for <strong>{reviewItem.title}</strong>?</p>
            <form onSubmit={handleSubmitReview}>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '24px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button key={star} type="button" onClick={() => setRating(star)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '48px', padding: 0, color: star <= rating ? '#f59e0b' : '#e2e8f0', transition: 'transform 0.1s' }} onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'} onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}>★</button>
                ))}
              </div>
              <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Leave an optional comment for the seller..." rows="4" style={{ width: '100%', padding: '16px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '15px', marginBottom: '24px', boxSizing: 'border-box', resize: 'vertical', outline: 'none' }} onFocus={handleFocus} onBlur={handleBlur} />
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" onClick={() => setShowReviewModal(false)} style={{ flex: 1, padding: '14px', backgroundColor: 'transparent', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={submittingReview} style={{ flex: 2, padding: '14px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: submittingReview ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)' }}>{submittingReview ? 'Publishing...' : 'Publish Review'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// Reusable Styles
const tabStyle = (isActive) => ({
  padding: '14px 20px', 
  backgroundColor: isActive ? '#f8fafc' : 'transparent', 
  color: isActive ? '#4f46e5' : '#475569', 
  border: isActive ? '1px solid #e2e8f0' : '1px solid transparent', 
  borderRadius: '12px', 
  fontWeight: isActive ? '800' : '600', 
  fontSize: '15px', 
  cursor: 'pointer', 
  textAlign: 'left', 
  transition: 'all 0.2s',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const badgeStyle = {
  backgroundColor: '#e2e8f0',
  color: '#475569',
  padding: '2px 8px',
  borderRadius: '50px',
  fontSize: '12px',
  fontWeight: '800'
};

const labelStyle = { 
  display: 'block', 
  marginBottom: '8px', 
  fontWeight: '700', 
  fontSize: '13px', 
  color: '#0f172a',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const inputStyle = { 
  width: '100%', 
  padding: '14px 16px', 
  borderRadius: '12px', 
  border: '1px solid #cbd5e1', 
  backgroundColor: '#f8fafc', 
  fontSize: '15px', 
  color: '#0f172a', 
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'all 0.2s ease'
};

const handleFocus = (e) => {
  e.target.style.backgroundColor = 'white';
  e.target.style.borderColor = '#4f46e5';
  e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
};

const handleBlur = (e) => {
  e.target.style.backgroundColor = '#f8fafc';
  e.target.style.borderColor = '#cbd5e1';
  e.target.style.boxShadow = 'none';
};